// Contas da tela do Faturamento na Visão Geral (#6500): Alice e Ítalo.
//
// - Fila do dia pelo status da fatura (mesma régua de Faturas e Cobranças,
//   `etapaDaFatura`): para calcular = validação + cálculo; para emitir = calculada.
//   Parcela de acordo (AVULSO, com meses futuros) e UC de cobrança agrupada (a
//   Prefeitura, cobrada no boleto da Secretaria) ficam de fora, como no quadro.
// - Faturas para revisar: puladas com injeção na conta e um motivo que não vale
//   para a origem dessa injeção (regra da trava do pulo, pularCalculoInjecao.ts).
//   Ter injeção não quer dizer que foi de usina da Vigor: a tela mostra a origem.
// - Minha taxa: emitidas em até 1 dia útil e fora do prazo sem justificativa, só
//   com a 1ª emissão de cada fatura (metricasFaturamento.ts).
//
// Lógica pura.

import { etapaDaFatura } from '../etapaFluxoFatura.ts';
import { classificarInjecao, motivosPermitidos, type ClassificacaoInjecao, type FaturaInjecao, type UsinasVigor } from '../pularCalculoInjecao.ts';
import { codigoMotivoEfetivo, rotuloMotivoPularCalculo } from '../motivoPularCalculo.ts';
import { contaComoFaturamento, emissaoForaDoPrazo, type EmissaoFaturamento } from '../financeiro/metricasFaturamento.ts';

export const META_ATE_1_DIA_UTIL = 0.97;
export const META_FORA_SEM_JUSTIFICATIVA = 0.005;
export const META_COBERTURA = 0.99;

// ── Fila do dia ──

export interface FaturaDaFila {
  status_fatura: string | null;
  classificacao: string | null;
  cobranca_agrupada?: boolean | null;
  pular_calculo_em?: string | null;
}

const foraDaFila = (f: FaturaDaFila) =>
  (f.classificacao || '').trim().toUpperCase() === 'AVULSO' || !!f.cobranca_agrupada || !!f.pular_calculo_em;

export interface FilaDoFaturamento {
  paraValidar: number;
  paraCalcular: number;
  paraEmitir: number;
}

export function filaDoFaturamento(faturas: readonly FaturaDaFila[]): FilaDoFaturamento {
  const r = { paraValidar: 0, paraCalcular: 0, paraEmitir: 0 };
  for (const f of faturas) {
    if (foraDaFila(f)) continue;
    const etapa = etapaDaFatura(f.status_fatura);
    if (etapa === 'validacao') r.paraValidar++;
    else if (etapa === 'calculo') r.paraCalcular++;
    else if (etapa === 'emissao') r.paraEmitir++;
  }
  return r;
}

// ── Faturas para revisar ──

export interface FaturaPulada extends FaturaInjecao {
  id: number;
  uc_codigo: string | null;
  cliente_nome: string | null;
  mes_referencia: string | null;
  pular_calculo_em: string | null;
  pular_calculo_por_nome?: string | null;
  motivo_pular_calculo?: string | null;
  motivo_pular_calculo_codigo?: string | null;
}

export interface FaturaParaRevisar {
  fatura: FaturaPulada;
  classificacao: ClassificacaoInjecao;
  /** "usina da Vigor" (C) ou "usina de terceiro" (B). */
  origem: 'vigor' | 'terceiro';
  motivo: string;
}

/**
 * Puladas com injeção cujo motivo não vale para a origem da injeção: "sem
 * injeção" com injeção na conta, ou motivo de terceiro com usina da Vigor. Sem a
 * lista de usinas da Vigor não dá para separar: devolve null.
 */
export function faturasParaRevisar(faturas: readonly FaturaPulada[], usinas: UsinasVigor | null): FaturaParaRevisar[] | null {
  if (!usinas) return null;
  const out: FaturaParaRevisar[] = [];
  for (const f of faturas) {
    if (!f.pular_calculo_em) continue;
    const c = classificarInjecao(f, usinas);
    if (c.situacao === 'A') continue;
    const codigo = codigoMotivoEfetivo(f as any);
    if (codigo && motivosPermitidos(c.situacao).includes(codigo)) continue;
    out.push({
      fatura: f,
      classificacao: c,
      origem: c.situacao === 'C' ? 'vigor' : 'terceiro',
      motivo: codigo ? rotuloMotivoPularCalculo(codigo) : (f.motivo_pular_calculo || 'Sem motivo'),
    });
  }
  // Usina da Vigor primeiro (é boleto que a Vigor deixou de emitir), depois o maior kWh.
  return out.sort((a, b) => (a.origem === b.origem ? 0 : a.origem === 'vigor' ? -1 : 1) || b.classificacao.kwh_total - a.classificacao.kwh_total);
}

// ── Minha taxa ──

/** Dias úteis (seg–sex) de `inicio` até `fim`, sem contar o dia de `inicio`. Mesmo dia = 0. */
export function diasUteisEntre(inicio: Date, fim: Date): number {
  const a = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
  const b = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate());
  let n = 0;
  for (const d = new Date(a); d < b;) {
    d.setDate(d.getDate() + 1);
    const dia = d.getDay();
    if (dia !== 0 && dia !== 6) n++;
  }
  return n;
}

const nomeComparavel = (v: string | null | undefined) =>
  (v || '').normalize('NFD').replace(/\p{Mn}/gu, '').trim().toLowerCase();

export interface MinhasEmissoes {
  /** 1ª emissão de cada fatura no período, pela pessoa (sem avulso). */
  total: number;
  /** Com a data de chegada da fatura conhecida. */
  comChegada: number;
  ate1DiaUtil: number;
  foraDoPrazo: number;
  foraSemJustificativa: number;
  /** 'AAAA-MM-DD' → boletos emitidos no dia. */
  porDia: Map<string, number>;
}

const diaLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** As emissões da pessoa entre `inicio` (incluso) e `fim` (exclusivo). `nome` null = o time todo. */
export function minhasEmissoes(emissoes: readonly EmissaoFaturamento[], nome: string | null, inicio: Date, fim: Date): MinhasEmissoes {
  const alvo = nome ? nomeComparavel(nome) : null;
  const r: MinhasEmissoes = { total: 0, comChegada: 0, ate1DiaUtil: 0, foraDoPrazo: 0, foraSemJustificativa: 0, porDia: new Map() };
  for (const e of emissoes) {
    if (!contaComoFaturamento(e)) continue;
    if (alvo && nomeComparavel(e.emitidoPorNome) !== alvo) continue;
    const quando = new Date(e.emitidoEm);
    if (isNaN(quando.getTime()) || quando < inicio || quando >= fim) continue;
    r.total++;
    const dia = diaLocal(quando);
    r.porDia.set(dia, (r.porDia.get(dia) ?? 0) + 1);
    if (e.disponivelEm) {
      const chegou = new Date(e.disponivelEm);
      if (!isNaN(chegou.getTime())) {
        r.comChegada++;
        if (diasUteisEntre(chegou, quando) <= 1) r.ate1DiaUtil++;
      }
    }
    if (emissaoForaDoPrazo(e)) {
      r.foraDoPrazo++;
      if (!(e.motivo || '').trim() && !(e.justificativa || '').trim()) r.foraSemJustificativa++;
    }
  }
  return r;
}

/** UCs do rateio faturadas ou puladas com motivo ÷ UCs do rateio (Conferência do Faturamento). */
export function coberturaDoFaturamento(r: { base: number; faturado: number; puladoJustificado: number }): number | null {
  return r.base > 0 ? (r.faturado + r.puladoJustificado) / r.base : null;
}
